import {
  Box,
  Typography,
  Paper,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  IconButton,
  CircularProgress,
} from "@mui/material";
import { Description, Close, Visibility } from "@mui/icons-material";
import { UploadedFile } from "../types/loan";

interface DocumentUploadSectionProps {
  title?: string;
  selectedFiles: File[];
  uploadedFiles: UploadedFile[];
  onFileSelect: (files: File[]) => void;
  onFileRemove: (index: number) => void;
  onPreview: (url: string) => void;
  isLoading?: boolean;
  uploadedTitle?: string;
}

export const DocumentUploadSection = ({
  title,
  selectedFiles,
  uploadedFiles,
  onFileSelect,
  onFileRemove,
  onPreview,
  isLoading = false,
  uploadedTitle = "Uploaded Documents",
}: DocumentUploadSectionProps) => {
  return (
    <Box>
      {title && (
        <Typography variant="h6" gutterBottom fontSize={"0.75rem"}>
          {title}
        </Typography>
      )}
      <Box
        sx={{
          border: "0.5px dashed #ccc",
          borderRadius: 1,
          p: 1,
          m: 1,
          height: "auto",
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          cursor: "pointer",
          backgroundColor: "#fafafa",
          "&:hover": {
            borderColor: "primary.main",
            backgroundColor: "#f5f5f5",
          },
        }}
        component="label"
      >
        <input
          type="file"
          multiple
          accept=".pdf,.jpg,.jpeg,.png"
          onChange={(e) => {
            if (e.target.files) {
              onFileSelect(Array.from(e.target.files));
            }
          }}
          style={{ display: "none" }}
        />
        <Typography color="textSecondary" sx={{ fontSize: "0.75rem" }}>
          Click to select documents
        </Typography>
        <Typography
          variant="caption"
          color="textSecondary"
          sx={{ mt: 0.5, fontSize: "0.65rem", fontStyle: "italic" }}
        >
          Supported formats: PDF, JPG, PNG
        </Typography>
      </Box>

      {/* Selected Files */}
      {selectedFiles.length > 0 && (
        <Box mb={1}>
          <Typography variant="subtitle2" gutterBottom fontSize={"0.75rem"}>
            Selected Documents
          </Typography>
          <Paper variant="outlined">
            <List dense disablePadding>
              {selectedFiles.map((file, index) => (
                <ListItem
                  key={`selected-${index}`}
                  secondaryAction={
                    <>
                      <IconButton
                        edge="end"
                        onClick={() => onPreview(URL.createObjectURL(file))}
                      >
                        <Visibility style={{ fontSize: "1rem" }} />
                      </IconButton>
                      <IconButton
                        edge="end"
                        onClick={() => onFileRemove(index)}
                        style={{ marginLeft: "0.5rem" }}
                      >
                        <Close style={{ fontSize: "1rem" }} />
                      </IconButton>
                    </>
                  }
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Description style={{ fontSize: "1rem" }} />
                    <Typography variant="body2" fontSize={"0.75rem"}>
                      {file.name}
                    </Typography>
                  </Box>
                </ListItem>
              ))}
            </List>
          </Paper>
        </Box>
      )}

      {/* Uploaded Files */}
      {uploadedFiles.length > 0 && (
        <Box>
          <Typography variant="subtitle2" gutterBottom fontSize={"0.75rem"}>
            {uploadedTitle}
          </Typography>
          <Paper variant="outlined">
            <List dense disablePadding>
              {uploadedFiles.map((file) => (
                <ListItem
                  key={file.id}
                  secondaryAction={
                    <IconButton
                      edge="end"
                      onClick={() => onPreview(file.presignedUrl)}
                    >
                      <Visibility style={{ fontSize: "1rem" }} />
                    </IconButton>
                  }
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Description style={{ fontSize: "1rem" }} />
                    <Typography variant="body2" fontSize={"0.75rem"}>
                      {file.fileName}
                    </Typography>
                  </Box>
                </ListItem>
              ))}
            </List>
          </Paper>
        </Box>
      )}

      {isLoading && (
        <Box display="flex" justifyContent="center" mt={2}>
          <CircularProgress size={24} />
        </Box>
      )}
    </Box>
  );
};
